import { Edit2, ShoppingCart, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useInventory } from "../hooks/useInventory";
import { useShoppingList } from "../hooks/useShoppingList";
import {
  groupByCategory,
  type SortPreference,
  sortItems,
} from "../lib/sorting";
import type { InventoryItem } from "../services/inventoryService";
import ItemForm from "./ItemForm";

interface InventoryTabProps {
  location: "fridge" | "freezer" | "pantry" | "other";
  selectedCategories: string[];
  searchQuery: string;
  sort: SortPreference;
}

// Render inventory items for a specific location and provide actions to edit, delete,
// or add items to the shopping list while showing brief UI feedback
export default function InventoryTab({
  location,
  selectedCategories,
  searchQuery,
  sort,
}: InventoryTabProps) {
  const { items, isLoading, deleteItem } = useInventory(location);
  const { addItem: addItemToShoppingList } = useShoppingList();
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [animatingIds, setAnimatingIds] = useState<string[]>([]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const matchesCategory =
        selectedCategories.length === 0 ||
        (item.category && selectedCategories.includes(item.category));
      const matchesSearch =
        normalizedQuery === "" ||
        item.item_name.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesSearch;
    });
  }, [items, selectedCategories, searchQuery]);

  const sortedItems = useMemo(
    () => sortItems(filteredItems, sort),
    [filteredItems, sort],
  );

  const groupedItems = useMemo(
    () => (sort.mode === "category" ? groupByCategory(filteredItems) : null),
    [filteredItems, sort.mode],
  );

  const handleDeleteItem = async (id: string) => {
    try {
      await deleteItem(id);
    } catch (error) {
      console.error("🔴 Failed to delete item:", error);
    }
  };

  const handleAddToShoppingList = async (item: InventoryItem) => {
    try {
      await addItemToShoppingList({
        name: item.item_name,
        quantity: item.quantity,
        unit: item.unit,
        category: item.category,
      });
      toast.success(`"${item.item_name}" added to shopping list.`);
    } catch (error) {
      console.error("🔴 Failed to add to shopping list:", error);
      toast.error("Failed to add to shopping list. Please try again.");
    }
  };

  const handleIconAnimation = (id: string) => {
    setAnimatingIds((prev) => prev.filter((i) => i !== id));
    requestAnimationFrame(() => {
      setAnimatingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    });
  };

  const handleIconAnimationEnd = (id: string) => {
    setAnimatingIds((prev) => prev.filter((i) => i !== id));
  };

  const renderItem = (item: InventoryItem) => (
    <div
      key={item.id}
      className="rounded-lg bg-gray-50 p-4 shadow-sm hover:shadow-md dark:bg-gray-700"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-medium wrap-break-word text-gray-800 capitalize dark:text-gray-100">
            {item.item_name}
          </p>
          {sort.mode !== "category" && (
            <p className="text-xs text-gray-500 capitalize dark:text-gray-400">
              {item.category}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
              {item.quantity} {item.unit}
            </span>
          </div>
        </div>
        <div className="ml-4 flex gap-2 self-start">
          <button
            type="button"
            onClick={() => setEditingItem(item)}
            className="shrink-0 rounded-lg p-2 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-600"
            aria-label={`Edit ${item.item_name}`}
          >
            <Edit2 className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => {
              handleIconAnimation(item.id);
              handleAddToShoppingList(item);
            }}
            className="shrink-0 overflow-hidden rounded-lg p-2 text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-gray-600"
            aria-label={`Add ${item.item_name} to shopping list`}
          >
            <ShoppingCart
              // attach animation end to the element with the animation
              onAnimationEnd={() => handleIconAnimationEnd(item.id)}
              className={`h-4 w-4 ${animatingIds.includes(item.id) ? "animate-cart-swoosh" : ""}`}
            />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteItem(item.id)}
            className="shrink-0 rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-gray-600"
            aria-label={`Delete ${item.item_name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {isLoading ? `Loading ${location} items...` : ""}
        {!isLoading && filteredItems.length > 0
          ? `${filteredItems.length} item${filteredItems.length !== 1 ? "s" : ""} in ${location}`
          : ""}
      </output>

      <div className="space-y-3">
        {filteredItems.length === 0 ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            <p>
              No items in {location}
              {selectedCategories.length > 0 || searchQuery.trim() !== ""
                ? " match your filters"
                : ""}
            </p>
          </div>
        ) : groupedItems ? (
          <div className="space-y-5">
            {groupedItems.map((group) => (
              <div key={group.category}>
                <h4 className="mb-2 text-xs font-bold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                  {group.category.replace("_", " ")}
                </h4>
                <div className="space-y-3">
                  {group.items.map((item) => renderItem(item))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          sortedItems.map((item) => renderItem(item))
        )}
      </div>
      {editingItem && (
        <ItemForm
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onItemAdded={() => setEditingItem(null)}
        />
      )}
    </>
  );
}
